import { ClipboardPlus, Search, Plus } from 'lucide-react';
import React, { useState, useEffect, useCallback } from 'react';
import DataTablePagination from '../../DataTablePagination.jsx';
import Spacing from '../../Spacing.jsx';
import { useDocumentTitle } from "../../../hooks/customDocumentTitleHook.jsx";
import { can_visit, debounce, getPractice, notify, numberWithCommas } from "../../../helpers/index.js";
import { deleteDocflowCategory, fetchDocflowTemplates } from "../../../services/aiCharting.js";
import DocflowCategoryDataTable from '../../DataTable/DocflowCategoryDataTable.jsx';
import AddAndEditDocflowCategoryModal from '../../Modal/AddAndEditDocflowCategoryModal.jsx';
import DeleteModal from '../../Modal/DeleteModal.jsx';


export default function DocflowCategory({selectedPracticeChange, fetchChangePracticeData}) {
  let location_id = getPractice("location_id");
  let account_id = getPractice("account_id");
  if(selectedPracticeChange != null && fetchChangePracticeData){
    location_id = selectedPracticeChange?.location_id;
    account_id = selectedPracticeChange?.account_id;
  }

  const [dataLoading, setDataLoading] = useState(true);
  const [docflowCategory, setDocflowCategory] = useState([]);
  const [openNewCategoryModal, setOpenNewCategoryModal] = useState(false);
  const [editDocflowModalOpen, setEditDocflowModalOpen] = useState(false);
  const [docflowEditData, setDocflowEditData] = useState({});
  const [docflowDeletedId, setDocflowDeletedId] = useState(null);
  const [activeDeleteModal, setActiveDeleteModal] = useState(false);
  const [previousScrollPosition, setPreviousScrollPosition] = useState(0);

  const defaultFilter = {
    page: 1,
    per_page: 10,
    account_id: account_id,
    location_id: location_id,
    status: "",
    search_term: "",
    order_by: []
  };
  const [filter, setFilter] = useState(defaultFilter);

  const [paginationCount, setPaginationCount] = useState({
    totalCount: 0,
    totalPages: 0,
  });

  const loadDocflowCategory = async (filter) => {
    let isCanceled = false;

    try {
      const response = await fetchDocflowTemplates(filter);
      if(response?.data) {
        setDocflowCategory(response?.data?.results);

        setPaginationCount({
          totalCount: response?.data?.count,
          totalPages: response?.data?.total_pages,
        });
      }
    } catch (error) {
      if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
        isCanceled = true;
      } else {
        // console.log("🚀 ~ loadDocflowCategory ~ error:", error);
      }
    } finally {
      if (!isCanceled) {
        setDataLoading(false);
      }
    }
  };

  useEffect(() => {
    loadDocflowCategory(filter);
  }, [filter]);


  const debouncedSetFilter = useCallback(
    debounce((newFilter) => setFilter(newFilter), 300), []
  );

  const handleChangeSearchTerm = (event) => {
    const newFilter = { ...filter, search_term: event.target.value, page: 1 };
    setDataLoading(true);
    debouncedSetFilter(newFilter);
  };

  const handlePageChange = (newPage) => {
    setFilter({ ...filter, page: newPage });
    setDataLoading(true);
  };

  const handleChangeRowsPerPage = (value) => {
    setFilter({
      ...filter,
      page: 1,
      per_page: value,
    });
    setDataLoading(true);
  };

  useEffect(() => {
    const sameLocation = filter.location_id === location_id;
    const sameAccount = filter.account_id === account_id;

    if (sameLocation && sameAccount) return;

    setFilter({
      ...filter,
      location_id: location_id,
      account_id: account_id,
    });
    setDataLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location_id, account_id]);

  const handleDeleteRecord = async (id) => {
    setDataLoading(true);
    try {
      if(can_visit('password_manager') && can_visit("password_manager_delete")) {
        const response = await deleteDocflowCategory( id, account_id, location_id );
        if(response){
          if(response?.detail) {
            notify("error", response?.detail);
          } else {
            if(response?.data?.status === "success") {
              notify("success", response?.data?.message, 0.5);
              await loadDocflowCategory(filter);
            } else {
              notify("error", response?.data?.message);
            }
          }
        }
      }
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.detail) {
        notify("error", error?.response?.data?.detail, 1);
      }
    } finally {
      setDataLoading(false);
    }
  };

  useDocumentTitle("Docflow Template");
  
  return (
    <>
      <div className="docpro_page_heading_wrap">
        <h2 className="docpro_page_heading docpro_fs_24 docpro_mb_0">
          <i className="docpro_iris_bg docpro_white_color">
            <ClipboardPlus width={20} height={20} />
          </i>
          Docflow Template
          <span>{numberWithCommas(paginationCount.totalCount)}</span>
        </h2>
        <div className="docpro_page_heading_btns">
          {can_visit('administrator_docflow_template') && (
            <button className="docpro_bordered_btn docpro_fw_600 docpro_white_color docpro_filed_btn"
              onClick={() => {
                setOpenNewCategoryModal(true); 
                setPreviousScrollPosition(window.scrollY);
                window.scrollTo(0, 0);
              }}
            >
              <Plus width={20} height={20} />
              <span>Add Template</span>
            </button>
          )}
        </div>
      </div>

      <Spacing lg="10" sm="15" />

      <div className="docpro_data_table_wrap">
        <div className="docpro_data_table_headings">
          <div className="flex items-center">
            <form className="docpro_search_form">
              <input
                type="text"
                placeholder="Search"
                className="docpro_search_input"
                onChange={handleChangeSearchTerm}
              />
              <button className="docpro_search_btn">
                <Search width={20} height={20} />
              </button>
            </form>
          </div>
        </div>

        <DocflowCategoryDataTable
          tableData={docflowCategory}
          reloadData={loadDocflowCategory}
          reloadDataFilter={filter}
          dataLoading={dataLoading}
          setDataLoading={setDataLoading}
          setEditDocflowModalOpen={setEditDocflowModalOpen}
          setDocflowEditData={setDocflowEditData}
          setActiveDeleteModal={setActiveDeleteModal}
          setDocflowDeletedId={setDocflowDeletedId}
          setPreviousScrollPosition={setPreviousScrollPosition}
        />

        {(docflowCategory.length > 0 && !dataLoading) && (
          <DataTablePagination
            currentPage={filter.page}
            totalPages={paginationCount.totalPages}
            onPageChange={handlePageChange}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            totalRecords={paginationCount.totalCount}
            currentPageData={filter.per_page}
          />
        )}
      </div>

      {openNewCategoryModal &&
        <AddAndEditDocflowCategoryModal
          account_id = {account_id}
          location_id = {location_id}
          reloadData = {loadDocflowCategory}
          reloadDataFilter = {filter}
          editModal = {false}
          activeModal = {openNewCategoryModal}
          setActiveModal = {setOpenNewCategoryModal}
          previousScrollPosition={previousScrollPosition}
        />
      }

      {editDocflowModalOpen &&
        <AddAndEditDocflowCategoryModal
          account_id = {account_id}
          location_id = {location_id}
          reloadData = {loadDocflowCategory}
          reloadDataFilter = {filter}
          editModal = {true}
          editValue = {docflowEditData}
          activeModal = {editDocflowModalOpen}
          setActiveModal = {setEditDocflowModalOpen}
          previousScrollPosition={previousScrollPosition}
        />
      }

      {activeDeleteModal && (
        <DeleteModal
          handleDelete={() => handleDeleteRecord(docflowDeletedId)}
          name="this template"
          activeModal={activeDeleteModal}
          setActiveModal={setActiveDeleteModal}
          previousScrollPosition={previousScrollPosition}
          setPreviousScrollPosition={setPreviousScrollPosition}
        />
      )}
    </>
  );
}
